import { CommentReplies } from "components/common/CommentReplies";
import { UnifiedThreadComment } from "components/common/UnifiedThreadComment";
import { Separator } from "components/elements";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatFullTimestamp, formatRelativeTime } from "lib/relativeTime";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useRef, useState } from "react";
import type { ThreadTimelineItem } from "@compass/shared/schema/api/threadTimeline";

/**
 * A run of same-kind events that sat next to each other in the stream, shown
 * as one chip ("Alice added Bob and Carol") instead of three.
 *
 * The server emits atomic events in order; collapsing them is presentation, so
 * it stays here — mobile will want different copy.
 */
type EventRun = {
    kind: "eventRun";
    type: Exclude<ThreadTimelineItem["type"], "message">;
    id: string;
    at: string;
    items: Extract<ThreadTimelineItem, { subjects: unknown[] }>[];
};

type Row = Extract<ThreadTimelineItem, { type: "message" }> | EventRun;

/** Group consecutive same-type events; a message always breaks the run. */
const groupTimeline = (timeline: ThreadTimelineItem[]): Row[] => {
    const rows: Row[] = [];
    for (const item of timeline) {
        if (item.type === "message") {
            rows.push(item);
            continue;
        }
        const last = rows[rows.length - 1];
        if (last && "kind" in last && last.type === item.type) {
            last.items.push(item);
            last.at = item.at;
            continue;
        }
        rows.push({ kind: "eventRun", type: item.type, id: item.id, at: item.at, items: [item] });
    }
    return rows;
};

const VERB: Record<Exclude<ThreadTimelineItem["type"], "message">, string> = {
    participantAdded: "added",
    participantRemoved: "removed",
    messageDeleted: "deleted a message from",
};

/**
 * Turn a run into prose, grouped by actor so one person's back-to-back actions
 * read as a sentence. With no actor — an org-membership cascade, say — it goes
 * passive: "Bob was removed" / "Bob and Carol were removed".
 */
const formatRun = (run: EventRun): string => {
    const byActor = new Map<string, { actorName: string | null; targets: string[] }>();
    for (const item of run.items) {
        const actorName = item.actor && "name" in item.actor ? item.actor.name : null;
        const key = actorName ?? "system";
        const entry = byActor.get(key) ?? { actorName, targets: [] };
        for (const subject of item.subjects) entry.targets.push(subject.name);
        byActor.set(key, entry);
    }

    const list = (names: string[]): string => {
        if (names.length <= 1) return names[0] ?? "";
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
    };

    const verb = VERB[run.type];
    return [...byActor.values()]
        .map(({ actorName, targets }) => {
            const targetList = list(targets);
            if (actorName) {
                // "Phil deleted a message from Phil" reads badly — someone
                // deleting their own message is the common case.
                if (run.type === "messageDeleted" && targets.every((t) => t === actorName)) {
                    return `${actorName} deleted a message`;
                }
                return `${actorName} ${verb} ${targetList}`;
            }
            // Subject-verb agreement for actorless events.
            if (run.type === "messageDeleted") return `A message from ${targetList} was deleted`;
            const auxiliary = targets.length === 1 ? "was" : "were";
            return `${targetList} ${auxiliary} ${verb}`;
        })
        .join(" · ");
};

export interface DiscussionCommentsProps {
    className?: string;
    /** Thread comments from the API */
    comments: ApiThreadComment[];
    /** Discussion ID for building share URLs */
    discussionId?: number;
    /** Thread ID for creating replies */
    threadId?: number;
    /**
     * Messages and events already merged and ordered by the server. When
     * absent — the charts caller passes only `comments` — the component falls
     * back to rendering `comments` alone.
     */
    timeline?: ThreadTimelineItem[];
    /**
     * PRTL-1332: the comment that IS the discussion's body. It can't be
     * deleted on its own, so it gets no Delete action — offering one that
     * always fails is the bug this ticket was about. Server-computed; the
     * timeline pages newest-first, so the client can't identify it.
     */
    openingPostCommentId?: number | null;
}

/**
 * Discussion Comments Component
 * Renders comments for a discussion using the unified thread comment system
 */

export const DiscussionComments = ({
    className,
    comments,
    discussionId,
    threadId,
    timeline,
    openingPostCommentId,
}: DiscussionCommentsProps) => {
    const [editingComment, setEditingComment] = useState<ApiThreadComment | null>(null);
    const [replyingToComment, setReplyingToComment] = useState<ApiThreadComment | null>(null);
    const [expandedCommentIds, setExpandedCommentIds] = useState<Set<number>>(new Set());
    const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

    const replyFormClearRef = useRef<(() => void) | null>(null);
    const editFormClearRef = useRef<(() => void) | null>(null);

    const currentUser = useCurrentUser();
    const timeZone = useUserTimezone();
    const utils = trpc.useUtils();
    const { enqueueSnackbar } = useSnackbar();

    /**
     * PRTL-1332: every mutation here MUST surface its failure.
     *
     * None of them did, and the server refuses a delete outright when the
     * target is a discussion's opening post ("delete the whole discussion
     * instead"). With nothing rendering the rejection the confirm dialog just
     * sat there and the button snapped back to "Delete" — indistinguishable
     * from a dead button, which is exactly how it was reported, twice.
     *
     * The server's message is the useful one (it names the remedy), so show it
     * rather than a generic string, and keep a fallback for network failures
     * that arrive with no message at all.
     */
    const showMutationError = useCallback(
        (error: { message?: string | null } | null | undefined, fallback: string) => {
            const message = error?.message?.trim();
            enqueueSnackbar(message || fallback, { variant: "error" });
        },
        [enqueueSnackbar],
    );

    const updateMutation = trpc.threads.updateComment.useMutation();
    const deleteMutation = trpc.threads.deleteComment.useMutation();
    const replyMutation = trpc.threads.createComment.useMutation();

    const shareUrlBuilder = (commentId: number) =>
        discussionId
            ? `${window.location.origin}/discussions/${discussionId}/${commentId}`
            : undefined;

    const handleReplyClose = useCallback(() => {
        if (replyingToComment) {
            // Auto-expand the parent comment's replies after submitting
            setExpandedCommentIds((prev) => new Set(prev).add(replyingToComment.id));
        }
        setReplyingToComment(null);
    }, [replyingToComment]);

    const rows = useMemo(() => {
        const items: ThreadTimelineItem[] =
            timeline ??
            comments.map((comment) => ({
                type: "message" as const,
                id: `message:${comment.id}`,
                at: comment.createdAt,
                comment,
            }));
        // The server returns newest-first; this view reads oldest-first.
        return groupTimeline([...items].reverse());
    }, [timeline, comments]);

    return (
        <div className={cn("flex flex-col gap-6", className)}>
            {rows.map((row) => {
                if (row.type === "message") {
                    const comment = row.comment;
                    return (
                        <div
                            key={`comment-${comment.id}`}
                            className="flex flex-col pb-6 last:border-none"
                        >
                            <UnifiedThreadComment
                                comment={comment}
                                context="discussion"
                                currentUser={currentUser}
                                onEdit={() => setEditingComment(comment)}
                                onDelete={
                                    comment.id === openingPostCommentId
                                        ? undefined
                                        : () => setDeletingCommentId(comment.id)
                                }
                                onReply={undefined}
                                shareUrl={shareUrlBuilder(comment.id)}
                                idPrefix="message"
                                repliesSlot={
                                    threadId && (
                                        <CommentReplies
                                            comment={comment}
                                            context="discussion"
                                            threadId={threadId}
                                            currentUser={currentUser}
                                            onReply={(c) => setReplyingToComment(c)}
                                            onEdit={(c) => setEditingComment(c)}
                                            onDelete={(commentId) =>
                                                setDeletingCommentId(commentId)
                                            }
                                            shareUrlBuilder={shareUrlBuilder}
                                            forceExpanded={expandedCommentIds.has(comment.id)}
                                        />
                                    )
                                }
                            />
                            <Separator className="mx-[-24px] mt-3.5" />
                        </div>
                    );
                }

                // Unknown event kinds render nothing rather than crashing, so a
                // server that starts emitting a new type degrades gracefully on
                // an already-shipped client.
                if (!("kind" in row) || !(row.type in VERB)) return null;

                const text = formatRun(row);
                const relative = formatRelativeTime(row.at, timeZone);
                const tooltip = formatFullTimestamp(row.at, timeZone);
                return (
                    <div key={row.id} className="flex justify-center">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500 italic"
                            title={tooltip}
                        >
                            <span>{text}</span>
                            <span className="text-gray-400 not-italic">·</span>
                            <span className="text-gray-400 not-italic">{relative}</span>
                        </span>
                    </div>
                );
            })}

            {editingComment && (
                <div className="fixed inset-0 z-30 bg-slate-900/20">
                    <div className="fixed top-1/2 left-1/2 z-40 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <ThreadCommentForm
                            context="discussion"
                            mode="edit"
                            initialContentJson={editingComment.contentJson}
                            initialAttachments={editingComment.attachments}
                            clearFormRef={editFormClearRef}
                            onSubmit={({ contentJson, attachmentIds }) => {
                                updateMutation.mutate(
                                    {
                                        commentId: editingComment.id,
                                        contentJson,
                                        attachmentIds,
                                    },
                                    {
                                        onSuccess: () => {
                                            editFormClearRef.current?.();
                                            utils.threads.getComments.invalidate();
                                            utils.threads.getChildComments.invalidate();
                                            utils.discussions.list.invalidate();
                                            utils.discussions.getDiscussionView.invalidate({
                                                discussionId,
                                            });
                                            setEditingComment(null);
                                        },
                                        onError: (error) => {
                                            // Keep the editor open so the
                                            // author doesn't lose what they
                                            // typed.
                                            showMutationError(
                                                error,
                                                "Couldn't save your changes. Please try again.",
                                            );
                                        },
                                    },
                                );
                            }}
                            onCancel={() => setEditingComment(null)}
                            showCancel
                            isLoading={updateMutation.isPending}
                            submitLabel="Save Changes"
                            placeholder="Edit your comment..."
                        />
                    </div>
                </div>
            )}
            {replyingToComment && threadId && (
                <ThreadCommentForm
                    context="discussion"
                    replyingToComment={replyingToComment}
                    clearFormRef={replyFormClearRef}
                    onSubmit={({ contentJson, attachmentIds }) => {
                        replyMutation.mutate(
                            {
                                threadId,
                                contentJson,
                                attachmentIds,
                                parentCommentId: replyingToComment.id,
                            },
                            {
                                onSuccess: () => {
                                    replyFormClearRef.current?.();
                                    utils.threads.getComments.invalidate();
                                    utils.threads.getChildComments.invalidate();
                                    utils.discussions.list.invalidate();
                                    handleReplyClose();
                                },
                                onError: (error) => {
                                    // Form stays open and populated — a failed
                                    // send must never look like a sent one.
                                    showMutationError(
                                        error,
                                        "Couldn't send your reply. Please try again.",
                                    );
                                },
                            },
                        );
                    }}
                    onCancel={handleReplyClose}
                    showCancel
                    isLoading={replyMutation.isPending}
                    submitLabel="Send Message"
                    placeholder="Reply to this discussion"
                />
            )}

            {deletingCommentId !== null && (
                <div className="fixed inset-0 z-30 bg-slate-900/20">
                    <div className="fixed top-1/2 left-1/2 z-40 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <p className="mb-4 text-sm text-gray-700">
                            Are you sure you want to delete this comment?
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeletingCommentId(null)}
                                className="rounded px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    deleteMutation.mutate(
                                        { commentId: deletingCommentId },
                                        {
                                            onSuccess: () => {
                                                utils.threads.getComments.invalidate();
                                                utils.threads.getChildComments.invalidate();
                                                utils.discussions.list.invalidate();
                                                utils.discussions.getDiscussionView.invalidate({
                                                    discussionId,
                                                });
                                                setDeletingCommentId(null);
                                            },
                                            onError: (error) => {
                                                showMutationError(
                                                    error,
                                                    "Couldn't delete this message. Please try again.",
                                                );
                                                // Close the dialog: leaving it
                                                // open behind an error reads as
                                                // "still working", and the
                                                // opening-post case can never
                                                // succeed on retry.
                                                setDeletingCommentId(null);
                                            },
                                        },
                                    );
                                }}
                                className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
